import React, { FC, useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Container,
  Divider,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Skeleton,
  Typography,
  Chip,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  AccessTime as AccessTimeIcon,
  CalendarToday as CalendarTodayIcon,
  Group as GroupIcon,
  EventSeat as EventSeatIcon,
  Checklist as ChecklistIcon,
} from "@mui/icons-material";
import HourglassTopIcon from "@mui/icons-material/HourglassTop";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import PieChartOutlineIcon from "@mui/icons-material/PieChartOutline";
import BackgroundWrapper from "../BackgroundWrapper";
import {
  fetchBoards,
  getBoardsSortedByName,
  getRegistrationPhase,
  getTournoi,
} from "../../features/tournoi/tournoiSlice";
import { useAppDispatch, useAppSelector } from "../../store";
import { normalizeWaitlistCount, Tableau } from "../../types";

interface BoardListProps {}

const BoardList: FC<BoardListProps> = () => {
  const boards = useAppSelector(getBoardsSortedByName);
  const registrationPhase = useAppSelector(getRegistrationPhase);
  const tournoi = useAppSelector(getTournoi);
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [maxHeight, setMaxHeight] = useState<number>(600);
  const shouldHideRegistrationsCount =
    registrationPhase === "closed" || registrationPhase === "preRegistration";

  const tournamentLabel = tournoi?.label?.trim() || "Tournoi";

  const isDark = theme.palette.mode === "dark";

  const statCardSx = {
    borderRadius: 3,
    border: "1px solid",
    borderColor: "divider",
    background: isDark
      ? alpha(theme.palette.primary.main, 0.12)
      : alpha(theme.palette.primary.main, 0.06),
    height: "100%",
    boxShadow: isDark ? "0 8px 32px rgba(0,0,0,0.35)" : 2,
  } as const;

  const sectionOverlineSx = {
    fontWeight: 800,
    letterSpacing: "0.14em",
    color: isDark ? "secondary.light" : "primary.main",
    display: "block",
    mb: 1,
  } as const;

  const linearTrackBg = isDark
    ? alpha(theme.palette.common.white, 0.12)
    : alpha(theme.palette.common.black, 0.08);

  const linearBarBg = isDark
    ? theme.palette.secondary.light
    : theme.palette.primary.main;
  const tableHeaderLabelSx = {
    display: "flex",
    alignItems: "center",
    gap: 0.75,
    justifyContent: "center",
    whiteSpace: "nowrap",
  } as const;

  const overview = useMemo(() => {
    let totalPlaces = 0;
    let totalInscrits = 0;
    for (const b of boards) {
      totalPlaces += b.nbPlace;
      totalInscrits += shouldHideRegistrationsCount ? 0 : b.nbParticipants;
    }
    const fillRate =
      totalPlaces > 0
        ? Math.round((totalInscrits / totalPlaces) * 1000) / 10
        : 0;
    return { totalPlaces, totalInscrits, fillRate };
  }, [boards, shouldHideRegistrationsCount]);

  useEffect(() => {
    dispatch(fetchBoards());
  }, [dispatch]);

  useEffect(() => {
    const updateHeight = () => {
      const appBar = document.querySelector("nav.MuiAppBar-root");
      const appBarHeight = appBar?.getBoundingClientRect().height || 64;
      const padding = 44;
      setMaxHeight(window.innerHeight - appBarHeight - padding);
    };
    updateHeight();
    window.addEventListener("resize", updateHeight);
    return () => window.removeEventListener("resize", updateHeight);
  }, []);

  if (!boards || boards.length === 0) {
    return (
      <BackgroundWrapper>
        <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 } }}>
          <Stack spacing={2}>
            <Box>
              <Typography
                variant="overline"
                sx={{
                  fontWeight: 800,
                  letterSpacing: "0.14em",
                  color: isDark ? "secondary.light" : "primary.main",
                }}
              >
                Tournoi
              </Typography>
              <Typography
                variant="h4"
                component="h1"
                fontWeight={800}
                sx={{ mt: 0.5 }}
              >
                Tableaux — {tournamentLabel}
              </Typography>
            </Box>
            <LinearProgress aria-label="Chargement des tableaux" />
            <TableRowsLoader rowsNum={6} />
          </Stack>
        </Container>
      </BackgroundWrapper>
    );
  }

  return (
    <BackgroundWrapper>
      <Container
        maxWidth="lg"
        sx={{
          pt: { xs: 0.5, sm: 0.75 },
          pb: { xs: 2, sm: 3 },
          px: { xs: 2, sm: 3 },
        }}
      >
        <Stack spacing={2}>
          <Box>
            <Typography
              variant="overline"
              sx={{
                fontWeight: 800,
                letterSpacing: "0.14em",
                color: isDark ? "secondary.light" : "primary.main",
              }}
            >
              Tournoi
            </Typography>
            <Typography
              variant="h4"
              component="h1"
              fontWeight={800}
              sx={{ mt: 0.5 }}
            >
              Tableaux — {tournamentLabel}
            </Typography>
          </Box>

          <Box
            component="section"
            aria-labelledby="boards-stats-heading"
            sx={{ width: "100%" }}
          >
            <Typography
              id="boards-stats-heading"
              component="h2"
              variant="overline"
              sx={sectionOverlineSx}
            >
              Vue d’ensemble
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
                gap: 2,
                width: "100%",
              }}
            >
              <Card elevation={0} sx={statCardSx}>
                <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <PeopleOutlineIcon color="secondary" sx={{ fontSize: 36 }} />
                    <Box>
                      <Typography variant="h5" fontWeight={800}>
                        {overview.totalInscrits}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Inscriptions
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
              <Card elevation={0} sx={statCardSx}>
                <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <EventAvailableIcon color="secondary" sx={{ fontSize: 36 }} />
                    <Box>
                      <Typography variant="h5" fontWeight={800}>
                        {overview.totalPlaces}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Places disponibles
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
              <Card elevation={0} sx={statCardSx}>
                <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <PieChartOutlineIcon color="secondary" sx={{ fontSize: 36 }} />
                    <Box>
                      <Typography variant="h5" fontWeight={800}>
                        {overview.fillRate}%
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Taux de remplissage global
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Box>
          </Box>

          <Divider
            role="presentation"
            sx={{
              borderColor: "divider",
              borderBottomWidth: 2,
            }}
          />

          <Box
            component="section"
            aria-labelledby="boards-detail-heading"
            sx={{ width: "100%" }}
          >
            <Typography
              id="boards-detail-heading"
              component="h2"
              variant="overline"
              sx={{ ...sectionOverlineSx, mb: 0 }}
            >
              Détail par tableau
            </Typography>

            {isMobile ? (
              <Stack spacing={2} sx={{ mt: 2 }}>
                {boards.map((board) => {
                  const waitlistCount = normalizeWaitlistCount(board.waitlistCount);
                  const displayedNbParticipants = shouldHideRegistrationsCount
                    ? 0
                    : board.nbParticipants;
                  const nbPlace = board.nbPlace > 0 ? board.nbPlace : 1;
                  const percent = (100 * displayedNbParticipants) / nbPlace;
                  const tooltipText = `Tableau rempli à ${Math.round(
                    percent
                  )}%. Il reste ${
                    board.nbPlace - displayedNbParticipants
                  } places.`;

                  return (
                    <Card
                      key={board.name}
                      elevation={0}
                      sx={{
                        borderRadius: 3,
                        overflow: "hidden",
                        border: "1px solid",
                        borderColor: "divider",
                        boxShadow: isDark ? "0 8px 32px rgba(0,0,0,0.35)" : 2,
                      }}
                    >
                      <Box
                        sx={{
                          px: 2,
                          py: 1.25,
                          bgcolor: isDark ? "grey.900" : "background.paper",
                          backgroundImage: isDark
                            ? `linear-gradient(120deg, ${alpha(theme.palette.primary.main, 0.22)} 0%, ${theme.palette.grey[900]} 42%)`
                            : `linear-gradient(120deg, ${alpha(theme.palette.primary.light, 0.3)} 0%, ${theme.palette.background.paper} 42%)`,
                          borderBottom: 1,
                          borderColor: "divider",
                        }}
                      >
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          justifyContent="space-between"
                        >
                          <Typography variant="subtitle1" fontWeight={800}>
                            Tableau {board.name}
                          </Typography>
                          <Chip
                            size="small"
                            label={board.label}
                            sx={{ fontWeight: 700, maxWidth: "52%" }}
                            color="secondary"
                            variant={isDark ? "filled" : "outlined"}
                          />
                        </Stack>
                      </Box>
                      <Divider />
                      <CardContent sx={{ pt: 2 }}>
                        <Stack spacing={1.25}>
                          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                            <CalendarTodayIcon
                              fontSize="small"
                              color="secondary"
                              sx={{ mt: 0.15 }}
                            />
                            <Typography variant="body2" color="text.primary">
                              {new Date(board.date).toLocaleDateString("fr-FR", {
                                dateStyle: "full",
                              })}
                            </Typography>
                          </Box>

                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <AccessTimeIcon fontSize="small" color="secondary" />
                            <Typography variant="body2" color="text.primary">
                              Pointage jusqu’à{" "}
                              <Box component="span" fontWeight={700}>
                                {board.heureLimite}
                              </Box>
                            </Typography>
                          </Box>

                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <GroupIcon fontSize="small" color="secondary" />
                            <Typography variant="body2" color="text.primary">
                              <Box component="span" fontWeight={700}>
                                {displayedNbParticipants}
                              </Box>{" "}
                              inscrit{displayedNbParticipants > 1 ? "s" : ""}
                            </Typography>
                          </Box>

                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <EventSeatIcon fontSize="small" color="secondary" />
                            <Typography variant="body2" color="text.primary">
                              <Box component="span" fontWeight={700}>
                                {board.nbPlace}
                              </Box>{" "}
                              place{board.nbPlace > 1 ? "s" : ""}
                            </Typography>
                          </Box>

                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <HourglassTopIcon fontSize="small" color="secondary" />
                            <Typography variant="body2" color="text.primary">
                              <Box component="span" fontWeight={700}>
                                {waitlistCount}
                              </Box>{" "}
                              en liste d'attente
                            </Typography>
                          </Box>

                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Tooltip title={tooltipText}>
                              <LinearProgress
                                variant="determinate"
                                value={percent}
                                sx={{
                                  flexGrow: 1,
                                  height: 10,
                                  borderRadius: 5,
                                  backgroundColor: linearTrackBg,
                                  "& .MuiLinearProgress-bar": {
                                    borderRadius: 5,
                                    backgroundColor: linearBarBg,
                                  },
                                }}
                              />
                            </Tooltip>
                            <Chip
                              label={`${displayedNbParticipants}/${board.nbPlace}`}
                              size="small"
                              sx={{ fontWeight: 700, flexShrink: 0 }}
                              color="secondary"
                              variant={isDark ? "filled" : "outlined"}
                            />
                          </Box>
                        </Stack>
                      </CardContent>
                    </Card>
                  );
                })}
              </Stack>
            ) : (
              <Card
                elevation={0}
                sx={{
                  mt: 2,
                  borderRadius: 3,
                  overflow: "hidden",
                  border: "1px solid",
                  borderColor: "divider",
                  boxShadow: isDark ? "0 8px 32px rgba(0,0,0,0.35)" : 2,
                }}
              >
                <TableContainer
                  component={Box}
                  sx={{ overflow: "auto", maxHeight }}
                >
                  <Table stickyHeader size="small" sx={{ tableLayout: "fixed" }}>
                    <TableHead>
                      <TableRow
                        sx={{
                          bgcolor: isDark
                            ? alpha(theme.palette.common.black, 0.35)
                            : alpha(theme.palette.primary.main, 0.08),
                        }}
                      >
                        <TableCell
                          component="th"
                          scope="col"
                          sx={{ fontWeight: 800, py: 1, px: 2, width: 56 }}
                        >
                          <Typography
                            variant="body2"
                            fontWeight={800}
                            color="text.primary"
                            sx={{ justifyContent: "flex-start" }}
                          >
                            #
                          </Typography>
                        </TableCell>
                        <TableCell
                          component="th"
                          scope="col"
                          sx={{ fontWeight: 800, py: 1, px: 2, width: "26%" }}
                        >
                          <Typography
                            variant="body2"
                            fontWeight={800}
                            color="text.primary"
                            sx={{ ...tableHeaderLabelSx, justifyContent: "flex-start" }}
                          >
                            <ChecklistIcon fontSize="small" color="secondary" />
                            Nom
                          </Typography>
                        </TableCell>
                        <TableCell
                          component="th"
                          scope="col"
                          sx={{ fontWeight: 800, py: 1, px: 2, width: "18%" }}
                        >
                          <Typography
                            variant="body2"
                            fontWeight={800}
                            color="text.primary"
                            sx={{ ...tableHeaderLabelSx, justifyContent: "flex-start" }}
                          >
                            <CalendarTodayIcon fontSize="small" color="secondary" />
                            Jour
                          </Typography>
                        </TableCell>
                        <TableCell
                          component="th"
                          scope="col"
                          sx={{ fontWeight: 800, py: 1, px: 1.5, width: 128 }}
                        >
                          <Typography
                            variant="body2"
                            fontWeight={800}
                            color="text.primary"
                            sx={tableHeaderLabelSx}
                          >
                            <AccessTimeIcon fontSize="small" color="secondary" />
                            Fin pointage
                          </Typography>
                        </TableCell>
                        <TableCell
                          component="th"
                          scope="col"
                          align="center"
                          sx={{ fontWeight: 800, py: 1, px: 1.5, width: 96 }}
                        >
                          <Typography
                            variant="body2"
                            fontWeight={800}
                            color="text.primary"
                            sx={tableHeaderLabelSx}
                          >
                            <GroupIcon fontSize="small" color="secondary" />
                            Inscrits
                          </Typography>
                        </TableCell>
                        <TableCell
                          component="th"
                          scope="col"
                          align="center"
                          sx={{ fontWeight: 800, py: 1, px: 1.5, width: 96 }}
                        >
                          <Typography
                            variant="body2"
                            fontWeight={800}
                            color="text.primary"
                            sx={tableHeaderLabelSx}
                          >
                            <EventSeatIcon fontSize="small" color="secondary" />
                            Places
                          </Typography>
                        </TableCell>
                        <TableCell
                          component="th"
                          scope="col"
                          align="center"
                          sx={{ fontWeight: 800, py: 1, px: 1.5, width: 110 }}
                        >
                          <Tooltip title="Liste d'attente">
                            <Typography
                              variant="body2"
                              fontWeight={800}
                              color="text.primary"
                              sx={tableHeaderLabelSx}
                            >
                              <HourglassTopIcon fontSize="small" color="secondary" />
                              Attente
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell
                          component="th"
                          scope="col"
                          align="center"
                          sx={{ fontWeight: 800, py: 1, px: 2, width: 200 }}
                        >
                          <Typography
                            variant="body2"
                            fontWeight={800}
                            color="text.primary"
                            sx={tableHeaderLabelSx}
                          >
                            <PieChartOutlineIcon fontSize="small" color="secondary" />
                            Remplissage
                          </Typography>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {boards.map((board: Tableau) => {
                        const waitlistCount = normalizeWaitlistCount(
                          board.waitlistCount
                        );
                        const displayedNbParticipants = shouldHideRegistrationsCount
                          ? 0
                          : board.nbParticipants;
                        const nbPlace = board.nbPlace > 0 ? board.nbPlace : 1;
                        const percent = (100 * displayedNbParticipants) / nbPlace;
                        const tooltipText = `Tableau rempli à ${Math.round(
                          percent
                        )}%. Il reste ${
                          board.nbPlace - displayedNbParticipants
                        } places.`;

                        return (
                          <TableRow
                            key={board.name}
                            hover
                            sx={{
                              "&:nth-of-type(even)": {
                                bgcolor: isDark
                                  ? alpha(theme.palette.common.white, 0.04)
                                  : alpha(theme.palette.common.black, 0.025),
                              },
                            }}
                          >
                            <TableCell
                              sx={{
                                py: 0.85,
                                px: 2,
                                verticalAlign: "middle",
                                borderColor: "divider",
                              }}
                            >
                              <Typography
                                variant="body2"
                                fontWeight={800}
                                color="text.primary"
                              >
                                {board.name}
                              </Typography>
                            </TableCell>
                            <TableCell
                              sx={{
                                py: 0.85,
                                px: 2,
                                verticalAlign: "middle",
                                borderColor: "divider",
                              }}
                            >
                              <Typography
                                variant="body2"
                                fontWeight={700}
                                color="text.primary"
                                noWrap
                                title={board.label}
                              >
                                {board.label}
                              </Typography>
                            </TableCell>
                            <TableCell
                              sx={{
                                py: 0.85,
                                px: 2,
                                verticalAlign: "middle",
                                borderColor: "divider",
                              }}
                            >
                              <Typography
                                variant="body2"
                                color="text.primary"
                                noWrap
                                title={new Date(board.date).toLocaleDateString("fr-FR", {
                                  dateStyle: "full",
                                })}
                              >
                                {new Date(board.date).toLocaleDateString("fr-FR", {
                                  dateStyle: "full",
                                })}
                              </Typography>
                            </TableCell>
                            <TableCell
                              sx={{
                                py: 0.85,
                                px: 1.5,
                                verticalAlign: "middle",
                                borderColor: "divider",
                              }}
                            >
                              <Typography
                                variant="body2"
                                fontWeight={700}
                                color="text.primary"
                              >
                                {board.heureLimite}
                              </Typography>
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{
                                py: 0.85,
                                px: 1.5,
                                verticalAlign: "middle",
                                borderColor: "divider",
                              }}
                            >
                              <Typography
                                variant="body2"
                                fontWeight={700}
                                color="text.primary"
                                sx={{ fontVariantNumeric: "tabular-nums" }}
                              >
                                {displayedNbParticipants}
                              </Typography>
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{
                                py: 0.85,
                                px: 1.5,
                                verticalAlign: "middle",
                                borderColor: "divider",
                              }}
                            >
                              <Typography
                                variant="body2"
                                fontWeight={700}
                                color="text.primary"
                                sx={{ fontVariantNumeric: "tabular-nums" }}
                              >
                                {board.nbPlace}
                              </Typography>
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{
                                py: 0.85,
                                px: 1.5,
                                verticalAlign: "middle",
                                borderColor: "divider",
                              }}
                            >
                              <Typography
                                variant="body2"
                                fontWeight={700}
                                color="text.primary"
                                sx={{ fontVariantNumeric: "tabular-nums" }}
                              >
                                {waitlistCount}
                              </Typography>
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{
                                py: 0.85,
                                px: 2,
                                verticalAlign: "middle",
                                borderColor: "divider",
                              }}
                            >
                              <Tooltip title={tooltipText}>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: 1,
                                  }}
                                >
                                  <LinearProgress
                                    variant="determinate"
                                    value={percent}
                                    sx={{
                                      width: 96,
                                      height: 10,
                                      borderRadius: 5,
                                      backgroundColor: linearTrackBg,
                                      "& .MuiLinearProgress-bar": {
                                        borderRadius: 5,
                                        backgroundColor: linearBarBg,
                                      },
                                    }}
                                  />
                                  <Chip
                                    label={`${displayedNbParticipants}/${board.nbPlace}`}
                                    size="small"
                                    sx={{ fontWeight: 700 }}
                                    color="secondary"
                                    variant={isDark ? "filled" : "outlined"}
                                  />
                                </Box>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Card>
            )}
          </Box>
        </Stack>
      </Container>
    </BackgroundWrapper>
  );
};

const TableRowsLoader: React.FC<{ rowsNum: number }> = ({ rowsNum }) => (
  <Stack spacing={1.5}>
    {[...Array(rowsNum)].map((_, index) => (
      <Skeleton
        key={index}
        variant="rounded"
        height={88}
        sx={{ borderRadius: 3 }}
      />
    ))}
  </Stack>
);

export default BoardList;
